import React, { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import GetAllPayments from "../../hooks/fetch-hooks/fetch-all/get-all-payments";
import Modal from "../Modal";
import { DownLoadTable } from "../../utils/DownloadtablesInExcel";

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};


const formatDate = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString();
};

const formatAmount = (amount, currency = 'INR') => {
    if (amount === null || amount === undefined || amount === '') return '-';
    return `${currency || 'INR'} ${amount}`;
};

const getStatusColor = (status) => {
    switch (status) {
        case 'SUCCESS':
            return 'bg-green-100 text-green-700';
        case 'PENDING':
            return 'bg-yellow-100 text-yellow-700';
        case 'FAILED':
            return 'bg-red-100 text-red-700';
        case 'CANCELLED':
            return 'bg-gray-100 text-gray-700';
        case 'REFUNDED':
        case 'PARTIALLY_REFUNDED':
            return 'bg-purple-100 text-purple-700';
        default:
            return 'bg-blue-100 text-blue-700';
    }
};

const getArrayText = (value) => {
    if (!Array.isArray(value) || value.length === 0) return '-';
    return value.join(', ');
};

// Matches the API's max page size (Constants.MAX_SIZE); larger requests are clamped.
const EXPORT_PAGE_SIZE = 500;

// Date inputs are YYYY-MM-DD; send full-day bounds in the browser's local time as ISO strings.
const buildDateParams = ({ createdFrom, createdTo, paidFrom, paidTo }) => {
    const params = {};
    const start = (d) => new Date(`${d}T00:00:00`).toISOString();
    const end = (d) => new Date(`${d}T23:59:59.999`).toISOString();
    if (createdFrom) params.createdFrom = start(createdFrom);
    if (createdTo) params.createdTo = end(createdTo);
    if (paidFrom) params.paidFrom = start(paidFrom);
    if (paidTo) params.paidTo = end(paidTo);
    return params;
};

const EXPORT_HEADERS = [
    'Payment ID',
    'Gateway Transaction ID',
    'Payment Status',
    'Failure Reason',
    'Payment Amount',
    'Currency',
    'Payment Method',
    'Payment Source',
    'Gateway',
    'User ID',
    'Paid At',
    'Created At',
    'Item ID',
    'Item Status',
    'Order ID',
    'Quotation ID',
    'Invoice Type',
    'Invoice Number',
    'Sub Total',
    'Discount',
    'GST',
    'Item Total',
    'Patient Name',
    'Patient Phone',
    'Evital Inventory ID',
    'Evital Bill Number',
];

// One row per payment item; payment-level fields repeated on each row.
// If a payment has no items, emits one row with item columns blank.
const buildExportRows = (payment) => {
    const items = payment.paymentItems || [];

    const paymentCols = [
        payment.id ?? '-',
        payment.gatewayTransactionId || '-',
        payment.status || '-',
        payment.failureReason || '-',
        payment.amount ?? '-',
        payment.currency || 'INR',
        payment.paymentMethod || '-',
        payment.paymentSource || '-',
        payment.gatewayName || '-',
        payment.userId || '-',
        formatDate(payment.paidAt),
        formatDate(payment.createdAt),
    ];

    if (items.length === 0) {
        return [[...paymentCols, '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-']];
    }

    return items.map((item) => [
        ...paymentCols,
        item.id ?? '-',
        item.status || '-',
        item.orderId ?? '-',
        item.quotationId ?? '-',
        item.invoiceType || '-',
        item.metadata?.invoiceNumber || '-',
        item.subTotal ?? '-',
        item.discount ?? '-',
        item.gst ?? '-',
        item.totalAmount ?? '-',
        item.patientName || '-',
        item.patientPhone || '-',
        item.inventoryId || '-',
        item.billNo || '-',
    ]);
};

function PaymentsTable() {
    const query = useQuery();
    const [, setSearchParams] = useSearchParams();
    const [payments, setPayments] = useState([]);
    const [total, setTotal] = useState(0);
    const [pageindex, setPageIndex] = useState(query.get('page') || 1);
    const [pageSize, setPageSize] = useState(query.get('size') || 50);
    const [isLoading, setIsLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [search, setSearch] = useState('');
    const [searchBy, setSearchBy] = useState('');
    const [isExporting, setIsExporting] = useState(false);
    const EMPTY_DATE_FILTERS = { createdFrom: '', createdTo: '', paidFrom: '', paidTo: '' };
    const [dateFilters, setDateFilters] = useState(EMPTY_DATE_FILTERS);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const debounceRef = useRef(null);

    const SEARCH_DEBOUNCE_MS = 400;

    const maxValues = Math.max(1, Math.ceil(total / Number(pageSize || 50)));

    const getData = async (searchValue = search, searchByValue = searchBy) => {
        setIsLoading(true);
        try {
            const params = { page: pageindex, size: pageSize };
            if (searchValue && searchByValue === 'paymentId') params.paymentId = searchValue;
            const response = await GetAllPayments(params);
            setPayments(response?.data?.data?.rows || []);
            setTotal(response?.data?.data?.count || 0);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to fetch payments');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getData();
    }, [pageindex, pageSize]);

    useEffect(() => {
        setSearchParams({ page: pageindex, size: pageSize });
    }, [pageindex, pageSize]);

    const handleSearchChange = (value) => {
        setSearch(value);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPageIndex(1);
            getData(value, searchBy);
        }, SEARCH_DEBOUNCE_MS);
    };

    const handleSearchByChange = (value) => {
        setSearchBy(value);
        setSearch('');
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const dateParams = buildDateParams(dateFilters);
            let allPayments;
            if (Object.keys(dateParams).length === 0) {
                // No date filter: export the same page/size the table is showing.
                const response = await GetAllPayments({ page: pageindex, size: pageSize });
                allPayments = response?.data?.data?.rows || [];
            } else {
                // Date filter: export every match, paging through at the API's max page size.
                allPayments = [];
                let page = 1;
                let matched = 0;
                do {
                    const response = await GetAllPayments({ page, size: EXPORT_PAGE_SIZE, ...dateParams });
                    const rows = response?.data?.data?.rows || [];
                    matched = response?.data?.data?.count || 0;
                    allPayments.push(...rows);
                    if (rows.length === 0) break;
                    page += 1;
                } while (allPayments.length < matched);
            }
            const tableData = allPayments.flatMap(buildExportRows);
            DownLoadTable('Payments-Export', 'Payments', EXPORT_HEADERS, tableData);
            setIsExportModalOpen(false);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to export payments');
        } finally {
            setIsExporting(false);
        }
    };

    const toggleExpanded = (paymentId) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(paymentId)) next.delete(paymentId);
            else next.add(paymentId);
            return next;
        });
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-800">Payments</h2>
                    <p className="text-sm text-gray-500">
                        {isLoading ? 'Loading payments...' : `${total} payment records`}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex rounded-lg border border-gray-300 bg-white overflow-hidden">
                        <select
                            value={searchBy}
                            onChange={(e) => handleSearchByChange(e.target.value)}
                            className="border-r border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-600 focus:outline-none"
                        >
                            <option value="">Search by...</option>
                            <option value="paymentId">Payment ID</option>
                        </select>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            placeholder={searchBy === 'paymentId' ? 'Enter payment ID...' : 'Select a filter...'}
                            disabled={!searchBy}
                            className="px-3 py-2 text-sm focus:outline-none disabled:bg-gray-50 disabled:text-gray-400 w-48"
                        />
                    </div>
                    <select
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(e.target.value);
                            setPageIndex(1);
                        }}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    >
                        <option value="25">25 per page</option>
                        <option value="50">50 per page</option>
                        <option value="100">100 per page</option>
                    </select>
                    <button
                        type="button"
                        onClick={() => setIsExportModalOpen(true)}
                        disabled={isLoading || total === 0}
                        className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white-50 hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                    >
                        Export Excel
                    </button>
                    <button
                        type="button"
                        onClick={() => getData()}
                        disabled={isLoading}
                        className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white-50 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isLoading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {isExportModalOpen && (
                <Modal
                    title="Export payments"
                    onClose={() => !isExporting && setIsExportModalOpen(false)}
                    contentClassName="sm:max-w-md md:max-w-md"
                >
                    <div className="space-y-4 text-sm text-gray-700">
                        {[
                            ['Created at', 'createdFrom', 'createdTo'],
                            ['Paid at', 'paidFrom', 'paidTo'],
                        ].map(([label, fromKey, toKey]) => (
                            <div key={label} className="space-y-1">
                                <p className="font-medium">{label}</p>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="date"
                                        value={dateFilters[fromKey]}
                                        max={dateFilters[toKey] || undefined}
                                        onChange={(e) => setDateFilters((prev) => ({ ...prev, [fromKey]: e.target.value }))}
                                        className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5"
                                    />
                                    <span>to</span>
                                    <input
                                        type="date"
                                        value={dateFilters[toKey]}
                                        min={dateFilters[fromKey] || undefined}
                                        onChange={(e) => setDateFilters((prev) => ({ ...prev, [toKey]: e.target.value }))}
                                        className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5"
                                    />
                                </div>
                            </div>
                        ))}
                        <p className="text-xs text-gray-500">
                            {Object.values(dateFilters).some(Boolean)
                                ? 'Exports all payments matching the selected dates.'
                                : 'No dates selected: exports the page currently shown in the table.'}
                        </p>
                        <div className="flex items-center justify-between pt-2">
                            <button
                                type="button"
                                onClick={() => setDateFilters(EMPTY_DATE_FILTERS)}
                                disabled={isExporting || !Object.values(dateFilters).some(Boolean)}
                                className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Clear dates
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                disabled={isExporting}
                                className="rounded-lg bg-green-600 px-4 py-2 font-medium text-white-50 hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                            >
                                {isExporting ? 'Exporting...' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            <div className="overflow-x-auto rounded-2xl bg-white shadow-soft-xl">
                <table className="table-auto w-full text-sm">
                    <thead>
                        <tr className="h-12 border-b-2 border-gray-100 bg-gray-50 text-left font-sans">
                            <th className="px-3 py-3">Payment</th>
                            <th className="px-3 py-3">Order / Quote</th>
                            <th className="px-3 py-3">User</th>
                            <th className="px-3 py-3">Amount</th>
                            <th className="px-3 py-3">Method</th>
                            <th className="px-3 py-3">Source</th>
                            <th className="px-3 py-3">Status</th>
                            <th className="px-3 py-3">Gateway</th>
                            <th className="px-3 py-3">Paid At</th>
                            <th className="px-3 py-3">Created</th>
                            <th className="px-3 py-3">Items</th>
                            <th className="px-3 py-3">Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {payments.map((payment) => {
                            const isExpanded = expandedRows.has(payment.id);
                            return (
                                <React.Fragment key={payment.id}>
                                    <tr className="border-t border-gray-100 align-top hover:bg-gray-50">
                                        <td className="px-3 py-4">
                                            <div className="font-semibold text-green-600">#{payment.id}</div>
                                            <div className="mt-1 text-xs text-gray-500">
                                                Txn: {payment.gatewayTransactionId || '-'}
                                            </div>
                                        </td>
                                        <td className="px-3 py-4">
                                            <div className="flex flex-col gap-1">
                                                {payment.orderId ? (
                                                    <a
                                                        href={`/order/edit/${payment.orderId}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-left font-semibold text-blue-600 hover:underline"
                                                    >
                                                        Order: {payment.orderId}
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-500">Order: {getArrayText(payment.orderIds)}</span>
                                                )}
                                                {payment.quotationId ? (
                                                    <a
                                                        href={`/quotation/edit/${payment.quotationId}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-left font-semibold text-blue-600 hover:underline"
                                                    >
                                                        Quote: {payment.quotationId}
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-500">Quote: {getArrayText(payment.quotationIds)}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-4">
                                            {payment.patientName && (
                                                <div className="font-semibold">{payment.patientName}</div>
                                            )}
                                            {payment.patientPhone && (
                                                <div className="text-xs text-gray-500">{payment.patientPhone}</div>
                                            )}
                                            <div className="text-xs text-gray-400 break-all">{payment.userId || '-'}</div>
                                        </td>
                                        <td className="px-3 py-4 font-semibold">
                                            {formatAmount(payment.amount, payment.currency)}
                                        </td>
                                        <td className="px-3 py-4">{payment.paymentMethod || '-'}</td>
                                        <td className="px-3 py-4">{payment.paymentSource || '-'}</td>
                                        <td className="px-3 py-4">
                                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(payment.status)}`}>
                                                {payment.status || '-'}
                                            </span>
                                            {payment.failureReason && (
                                                <div className="mt-2 max-w-[220px] text-xs text-red-600">
                                                    {payment.failureReason}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-4">
                                            <div>{payment.gatewayName || '-'}</div>
                                            <div className="mt-1 text-xs text-gray-500">Currency: {payment.currency || '-'}</div>
                                        </td>
                                        <td className="px-3 py-4">{formatDate(payment.paidAt)}</td>
                                        <td className="px-3 py-4">
                                            <div>{formatDate(payment.createdAt)}</div>
                                            <div className="mt-1 text-xs text-gray-500">Updated: {formatDate(payment.updatedAt)}</div>
                                        </td>
                                        <td className="px-3 py-4">
                                            <div className="font-semibold">{payment.paymentItems?.length || 0} items</div>
                                            <div className="mt-1 text-xs text-gray-500">
                                                Expires: {formatDate(payment.expiresAt)}
                                            </div>
                                        </td>
                                        <td className="px-3 py-4">
                                            <button
                                                type="button"
                                                onClick={() => toggleExpanded(payment.id)}
                                                className="rounded-lg border border-blue-600 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50"
                                            >
                                                {isExpanded ? 'Hide' : 'View'}
                                            </button>
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr className="border-t border-gray-100 bg-gray-50">
                                            <td colSpan="12" className="px-4 py-4">
                                                <div className="w-full rounded-xl border border-gray-200 bg-white p-4">
                                                    <div className="mb-3 font-semibold text-gray-800">Payment Items</div>
                                                    <table className="w-full text-sm">
                                                        <thead>
                                                            <tr className="border-b-2 border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                                                                <th className="px-3 py-3 font-bold">Item</th>
                                                                <th className="px-3 py-3 font-bold">Status</th>
                                                                <th className="px-3 py-3 font-bold">Order</th>
                                                                <th className="px-3 py-3 font-bold">Quote</th>
                                                                <th className="px-3 py-3 font-bold">Invoice</th>
                                                                <th className="px-3 py-3 font-bold">Inventory ID</th>
                                                                <th className="px-3 py-3 font-bold">Bill No</th>
                                                                <th className="px-3 py-3 font-bold">Patient Name</th>
                                                                <th className="px-3 py-3 font-bold">Patient Phone</th>
                                                                <th className="px-3 py-3 font-bold">Total</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(payment.paymentItems || []).map((item, idx) => (
                                                                <tr key={item.id} className={`align-middle ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                                                                    <td className="px-3 py-3 font-bold text-gray-800">#{item.id}</td>
                                                                    <td className="px-3 py-3">
                                                                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusColor(item.status)}`}>
                                                                            {item.status || '-'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-3 py-3">
                                                                        {item.orderId ? (
                                                                            <a href={`/order/edit/${item.orderId}`} target="_blank" rel="noreferrer" className="font-semibold text-blue-600 hover:underline">
                                                                                {item.orderId}
                                                                            </a>
                                                                        ) : <span className="text-gray-400">—</span>}
                                                                    </td>
                                                                    <td className="px-3 py-3">
                                                                        {item.quotationId ? (
                                                                            <a href={`/quotation/edit/${item.quotationId}`} target="_blank" rel="noreferrer" className="font-semibold text-blue-600 hover:underline">
                                                                                {item.quotationId}
                                                                            </a>
                                                                        ) : <span className="text-gray-400">—</span>}
                                                                    </td>
                                                                    <td className="px-3 py-3">
                                                                        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">{item.invoiceType || '—'}</span>
                                                                    </td>
                                                                    <td className="px-3 py-3">
                                                                        {item.inventoryId
                                                                            ? <a
                                                                                href={`https://pharmacy.evitalrx.in/sales/view/${item.inventoryId}`}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                className="font-mono text-xs font-semibold text-blue-600 hover:underline"
                                                                            >{item.inventoryId}</a>
                                                                            : <span className="text-gray-400">—</span>}
                                                                    </td>
                                                                    <td className="px-3 py-3 text-gray-600">{item.billNo || <span className="text-gray-400">—</span>}</td>
                                                                    <td className="px-3 py-3">
                                                                        {item.patientName
                                                                            ? <span
                                                                                className="cursor-pointer font-semibold text-gray-800 hover:text-blue-600"
                                                                                title="Click to copy"
                                                                                onClick={() => { navigator.clipboard.writeText(item.patientName); toast.success('Patient name copied'); }}
                                                                            >{item.patientName}</span>
                                                                            : <span className="text-gray-400">—</span>}
                                                                    </td>
                                                                    <td className="px-3 py-3">
                                                                        {item.patientPhone
                                                                            ? <span
                                                                                className="cursor-pointer text-gray-600 hover:text-blue-600"
                                                                                title="Click to copy"
                                                                                onClick={() => { navigator.clipboard.writeText(item.patientPhone); toast.success('Phone number copied'); }}
                                                                            >{item.patientPhone}</span>
                                                                            : <span className="text-gray-400">—</span>}
                                                                    </td>
                                                                    <td className="px-3 py-3 font-bold text-gray-800">{formatAmount(item.totalAmount, payment.currency)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    {(!payment.paymentItems || payment.paymentItems.length === 0) && (
                                                        <div className="text-sm text-gray-500">No payment items mapped.</div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {!isLoading && payments.length === 0 && (
                            <tr>
                                <td colSpan="12" className="px-3 py-12 text-center text-gray-500">
                                    No payments found
                                </td>
                            </tr>
                        )}
                        {isLoading && (
                            <tr>
                                <td colSpan="12" className="px-3 py-12 text-center text-gray-500">
                                    Loading payments...
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="btn_div">
                <button onClick={() => setPageIndex((count) => Number(count) - 1)} disabled={Number(pageindex) <= 1 || isLoading} className="previous">
                    Previous
                </button>
                <span className="pageindex">
                    Page {pageindex} of {maxValues}
                </span>
                <button
                    onClick={() => setPageIndex((count) => Number(count) + 1)}
                    disabled={Number(pageindex) >= maxValues || isLoading}
                    className="next"
                >
                    Next {maxValues}
                </button>
            </div>
        </div>
    );
}

export default PaymentsTable;
